import React, { useState } from 'react';
import ResumeDownload from './ResumeDownload';
import { useLanguage } from '../../context/LanguageContext';

import ReactOriginal from 'devicons-react/lib/icons/ReactOriginal';
import LaravelOriginalWordmark from 'devicons-react/lib/icons/LaravelOriginalWordmark';
import FlutterOriginal from 'devicons-react/lib/icons/FlutterOriginal';
import MysqlOriginal from 'devicons-react/lib/icons/MysqlOriginal';
import LinuxOriginal from 'devicons-react/lib/icons/LinuxOriginal';
import AmazonwebservicesOriginalWordmark from 'devicons-react/lib/icons/AmazonwebservicesOriginalWordmark';

export interface ExperienceProps { }

interface WorkExperienceItem {
    id: string;
    company: string;
    roleKey: string;
    periodKey: string;
    descKey: string;
    skills: string[];
    isCurrent?: boolean;
    badge?: string;
}

const WORK_EXPERIENCES: WorkExperienceItem[] = [
    {
        id: 'sistepos',
        company: 'Sistepos',
        roleKey: 'exp_role_sistepos',
        periodKey: 'exp_period_sistepos',
        descKey: 'exp_desc_sistepos',
        skills: ['React', 'JavaScript', 'AWS Lambda', 'AWS S3', 'Firebase', 'Electron'],
        isCurrent: true,
        badge: 'SaaS POS'
    },
    {
        id: 'plm',
        company: 'PLM Group',
        roleKey: 'exp_role_plm',
        periodKey: 'exp_period_plm',
        descKey: 'exp_desc_plm',
        skills: ['Flutter', 'React Native', 'WordPress', 'Gutenberg (React)', 'PHP', 'AWS EC2', 'Linux'],
        isCurrent: true,
        badge: 'Enterprise & Mobile'
    },
    {
        id: 'plastimedia',
        company: 'Plastimedia Studio',
        roleKey: 'exp_role_plasti',
        periodKey: 'exp_period_plasti',
        descKey: 'exp_desc_plasti',
        skills: ['Laravel', 'PHP', 'React', 'React Native', 'Shopify Liquid', 'MySQL'],
        badge: 'Leadership & Full Stack'
    },
    {
        id: 'invest',
        company: 'Invest in Oriente / Alcaldía de Rionegro',
        roleKey: 'exp_role_invest',
        periodKey: 'exp_period_invest',
        descKey: 'exp_desc_invest',
        skills: ['React Native', 'Laravel', 'MySQL', 'Gamification'],
        badge: 'Public Sector'
    },
    {
        id: 'fincli',
        company: 'FINCLI / RutaN / BID',
        roleKey: 'exp_role_fincli',
        periodKey: 'exp_period_fincli',
        descKey: 'exp_desc_fincli',
        skills: ['WordPress', 'PHP', 'JavaScript', 'LMS'],
        badge: 'Healthcare & E-learning'
    }
];

const Experience: React.FC<ExperienceProps> = () => {
    const { t } = useLanguage();
    const [activeTab, setActiveTab] = useState<'work' | 'skills'>('work');

    return (
        <section className="habilidades experiencia-view" id="habilidades">
            <div className="ancho">
                {/* Header */}
                <div className="exp-header-container">
                    <div>
                        <h2>{t('exp_title')}</h2>
                        <p className="exp-subtitle">{t('exp_subtitle')}</p>
                    </div>
                    <div className="exp-header-action">
                        <ResumeDownload variant="pill" />
                    </div>
                </div>

                {/* Tabs */}
                <div className="exp-tabs-nav">
                    <button
                        className={`exp-tab-btn ${activeTab === 'work' ? 'active' : ''}`}
                        onClick={() => setActiveTab('work')}
                    >
                        <i className="fa fa-briefcase" aria-hidden="true" style={{ marginRight: 8 }}></i>
                        {t('exp_tab_work')}
                    </button>
                    <button
                        className={`exp-tab-btn ${activeTab === 'skills' ? 'active' : ''}`}
                        onClick={() => setActiveTab('skills')}
                    >
                        <i className="fa fa-code" aria-hidden="true" style={{ marginRight: 8 }}></i>
                        {t('exp_tab_skills')}
                    </button>
                </div>

                {/* Tab Content: Work Experience */}
                {activeTab === 'work' && (
                    <div className="exp-timeline">
                        {WORK_EXPERIENCES.map((item) => (
                            <div key={item.id} className="exp-timeline-item">
                                <div className="exp-timeline-marker">
                                    <span className={`exp-dot ${item.isCurrent ? 'current' : ''}`}></span>
                                </div>
                                <div className="exp-card win7-project-card">
                                    <div className="exp-card-header">
                                        <div className="exp-card-titles">
                                            <div className="exp-company-row">
                                                <h3 className="exp-company-name">{item.company}</h3>
                                                {item.badge && <span className="exp-badge">{item.badge}</span>}
                                            </div>
                                            <p className="exp-role-title">{t(item.roleKey)}</p>
                                        </div>
                                        <span className="exp-period-tag">{t(item.periodKey)}</span>
                                    </div>

                                    <p className="exp-description">{t(item.descKey)}</p>

                                    <div className="exp-skills-pills">
                                        {item.skills.map((skill, sIdx) => (
                                            <span key={sIdx} className="exp-skill-pill">
                                                {skill}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Tab Content: Skills Matrix */}
                {activeTab === 'skills' && (
                    <div className="exp-skills-matrix">
                        {/* Cloud & DevOps */}
                        <div className="exp-skill-category-card">
                            <div className="exp-category-header">
                                <AmazonwebservicesOriginalWordmark size="40" />
                                <h3>{t('skill_cat_cloud')}</h3>
                            </div>
                            <div className="exp-skills-list">
                                <span className="exp-skill-tag highlight">AWS (EC2, S3, Lambda, CLI)</span>
                                <span className="exp-skill-tag highlight">Serverless</span>
                                <span className="exp-skill-tag">Firebase</span>
                                <span className="exp-skill-tag">Supabase</span>
                                <span className="exp-skill-tag">Linux / Terminal</span>
                                <span className="exp-skill-tag">Git / GitHub</span>
                            </div>
                        </div>

                        {/* Frontend Web */}
                        <div className="exp-skill-category-card">
                            <div className="exp-category-header">
                                <ReactOriginal size="40" />
                                <h3>{t('skill_cat_frontend')}</h3>
                            </div>
                            <div className="exp-skills-list">
                                <span className="exp-skill-tag highlight">React</span>
                                <span className="exp-skill-tag highlight">TypeScript</span>
                                <span className="exp-skill-tag">JavaScript (ES6+)</span>
                                <span className="exp-skill-tag">Three.js / 3D</span>
                                <span className="exp-skill-tag">Vite / Webpack</span>
                                <span className="exp-skill-tag">HTML5 & CSS3</span>
                            </div>
                        </div>

                        {/* Mobile */}
                        <div className="exp-skill-category-card">
                            <div className="exp-category-header">
                                <FlutterOriginal size="40" />
                                <h3>{t('skill_cat_mobile')}</h3>
                            </div>
                            <div className="exp-skills-list">
                                <span className="exp-skill-tag highlight">Flutter & Dart</span>
                                <span className="exp-skill-tag highlight">React Native</span>
                                <span className="exp-skill-tag">Expo</span>
                                <span className="exp-skill-tag">Offline-first Sync</span>
                                <span className="exp-skill-tag">Mobile UI/UX</span>
                            </div>
                        </div>

                        {/* Backend & CMS */}
                        <div className="exp-skill-category-card">
                            <div className="exp-category-header">
                                <LaravelOriginalWordmark size="40" />
                                <h3>{t('skill_cat_backend')}</h3>
                            </div>
                            <div className="exp-skills-list">
                                <span className="exp-skill-tag highlight">Laravel</span>
                                <span className="exp-skill-tag highlight">PHP</span>
                                <span className="exp-skill-tag">Node.js</span>
                                <span className="exp-skill-tag">WordPress (Custom Themes)</span>
                                <span className="exp-skill-tag">Gutenberg Blocks (React)</span>
                                <span className="exp-skill-tag">Shopify Liquid</span>
                            </div>
                        </div>

                        {/* Databases */}
                        <div className="exp-skill-category-card">
                            <div className="exp-category-header">
                                <MysqlOriginal size="40" />
                                <h3>{t('skill_cat_db')}</h3>
                            </div>
                            <div className="exp-skills-list">
                                <span className="exp-skill-tag highlight">MySQL</span>
                                <span className="exp-skill-tag">PostgreSQL</span>
                                <span className="exp-skill-tag">Firestore / Realtime DB</span>
                                <span className="exp-skill-tag">Supabase</span>
                            </div>
                        </div>

                        {/* Practices & Architecture */}
                        <div className="exp-skill-category-card">
                            <div className="exp-category-header">
                                <LinuxOriginal size="40" />
                                <h3>{t('skill_cat_practices')}</h3>
                            </div>
                            <div className="exp-skills-list">
                                <span className="exp-skill-tag highlight">Clean Architecture</span>
                                <span className="exp-skill-tag highlight">Análisis Causa Raíz (RCA)</span>
                                <span className="exp-skill-tag">Resolución de Incidentes</span>
                                <span className="exp-skill-tag">Metodologías Ágiles (Scrum)</span>
                                <span className="exp-skill-tag">Liderazgo de Equipos Técnicos</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Bottom Resume Download Banner */}
                <div className="exp-bottom-resume-wrapper">
                    <ResumeDownload variant="card" />
                </div>
            </div>
        </section>
    );
};

export default Experience;
